(function () {
  'use strict';

  if (window.KpSidecartComboCro) return;
  window.KpSidecartComboCro = true;

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    const maximumFractionDigits = Number.isInteger(value) ? 0 : 2;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits
    }).format(value).replace('₹', '₹\u00a0');
  };

  const utsavSavingsForItem = (item) => (item.line_level_discount_allocations || [])
    .filter((allocation) => ['UTSAV20', 'UTSAV OFFER'].includes(String(allocation.discount_application?.title || '').toUpperCase()))
    .reduce((sum, allocation) => sum + Number(allocation.amount || 0), 0);

  const actualSavingsForItem = (item) => Math.max(0,
    Number(item.original_line_price || 0) - Number(item.final_line_price ?? item.line_price ?? 0));

  let lastCartQualified = false;
  let checkoutDialogCount = 0;

  class SidecartComboCro {
    constructor(drawer) {
      this.drawer = drawer;
      delete this.drawer.dataset.kpUtsavCheckoutGuardReady;
      this.offer = drawer.querySelector('[data-kp-sidecart-offer]');
      this.recommendations = drawer.querySelector('[data-kp-sidecart-recommendations]');
      this.recommendationTrack = drawer.querySelector('[data-kp-sidecart-recommendations-track]');
      this.recommendationStatus = drawer.querySelector('[data-kp-sidecart-recommendations-status]');
      this.savings = drawer.querySelector('[data-kp-sidecart-savings]');
      this.hamperCard = drawer.querySelector('[data-kp-sidecart-hamper-card]');
      this.hamperAdd = drawer.querySelector('[data-kp-sidecart-hamper-add]');
      this.hamperQuantity = drawer.querySelector('[data-kp-sidecart-hamper-quantity]');
      this.hamperMinus = drawer.querySelector('[data-kp-sidecart-hamper-minus]');
      this.hamperPlus = drawer.querySelector('[data-kp-sidecart-hamper-plus]');
      this.cartContent = drawer.querySelector('.js-mini-cart-content');
      this.checkoutButton = drawer.querySelector('.js-cart-btn-checkout');
      this.minimumSubtotal = 699900;
      this.discountPercentage = Number(this.offer?.dataset.discountPercentage || 20);
      this.offerEndDate = this.offer?.dataset.offerEndDate || '';
      this.offerExpiryCopy = this.offer?.querySelector('[data-kp-sidecart-offer-expiry]');
      this.productCache = new Map();
      this.recommendationProducts = null;
      this.syncTimer = null;
      this.isSyncing = false;
      this.hasPendingSync = false;
      this.isMutating = false;
      this.lastCart = null;
      this.wasOpen = this.isOpen();

      if (!this.offer || !this.recommendations || !this.cartContent) return;
      this.bind();
      if (this.isOpen()) this.scheduleSync(0);
    }

    isOpen() {
      return this.drawer.classList.contains('active') || this.drawer.classList.contains('open');
    }

    bind() {
      new MutationObserver(() => {
        const open = this.isOpen();
        if (open && !this.wasOpen) this.scheduleSync(40);
        this.wasOpen = open;
      }).observe(this.drawer, { attributes: true, attributeFilter: ['class'] });

      new MutationObserver((mutations) => {
        const cartRowsWereRebuilt = mutations.some((mutation) => (
          Array.from(mutation.addedNodes || []).some((node) => (
            node.nodeType === 1
            && (node.matches?.('.mini-cart-item') || node.querySelector?.('.mini-cart-item'))
          ))
        ));
        if (cartRowsWereRebuilt && this.isOpen()) this.scheduleSync(60);
      }).observe(this.cartContent, { childList: true, subtree: true });

      this.cartContent.addEventListener('click', (event) => {
        if (!event.target.closest('.js-qty__adjust, .js-remove-mini-cart')) return;
        this.scheduleSync(700);
      });

      this.hamperAdd?.addEventListener('click', () => this.changeHamperQuantity(1));
      this.hamperMinus?.addEventListener('click', () => this.changeHamperQuantity(-1));
      this.hamperPlus?.addEventListener('click', () => this.changeHamperQuantity(1));

      document.addEventListener('kp:cart-updated', (event) => {
        const cart = event.detail?.cart || null;
        if (!this.isOpen()) {
          if (cart && Number(cart.item_count || 0) === 0) this.render(cart, []);
          return;
        }
        this.scheduleSync(40, cart);
      });

      if (!this.utsavCheckoutCapture) {
        this.utsavCheckoutCapture = (event) => {
          const button = event.target?.closest?.('.js-cart-btn-checkout');
          const journey = window.KpUtsavJourney;
          if (!button || !this.drawer.isConnected
            || button.closest('.js-mini-cart.cardraw') !== this.drawer) return;
          if (this.quantityQueueGate().blocked) {
            event.preventDefault(); event.stopImmediatePropagation();
            this.utsavCheckoutPermit = null;
            this.showQuantityReview(button);
            return;
          }
          if (button.classList.contains('disabled') || button.getAttribute('aria-disabled') === 'true') return;
          const permit = this.utsavCheckoutPermit;
          if (permit?.button === button && permit.synchronous === true) {
            permit.event = event;
            this.utsavCheckoutPermit = null;
            if (!journey?.isMutating?.() && journey?.cartRevision?.() === permit.revision) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showUtsavStatus('Your selection changed. Please review it and try again.');
            return;
          }
          event.preventDefault();
          event.stopImmediatePropagation();
          this.validateUtsavCheckout(button);
        };
        window.addEventListener('click', this.utsavCheckoutCapture, true);
      }
      this.drawer.dataset.kpUtsavCheckoutGuardReady = 'true';
    }

    quantityQueueGate() {
      const queue = window.KpMiniCartQuantityQueue;
      if (queue?.version !== 1 || queue.ready !== true
        || typeof queue.hasPendingChange !== 'function' || typeof queue.checkPendingChange !== 'function') {
        return { blocked: true, unavailable: true, message: 'Cart controls are not ready. Refresh the page or recheck the cart update.' };
      }
      try {
        const state = (typeof queue.pendingState === 'function' ? queue.pendingState() : null) || {};
        return { queue, blocked: queue.hasPendingChange() || state.busy === true
          || state.reviewRequired === true || state.uncertain === true, state };
      } catch (_) {
        return { blocked: true, unavailable: true, message: 'We could not verify the cart update. Refresh the page or recheck it.' };
      }
    }

    assertQuantityQueueSettled() {
      if (this.quantityQueueGate().blocked) throw Object.assign(new Error('Review your cart update before checking out.'), { quantityQueueBlocked: true });
    }

    showQuantityReview(button) {
      const gate = this.quantityQueueGate();
      if (!gate.blocked) { this.showUtsavStatus('Your cart update is checked. Review your current items and check out again.'); return; }
      this.showUtsavStatus('');
      const dialog = document.createElement('dialog');
      dialog.className = 'kp-utsav-offer-dialog';
      const id = `kp-cart-update-dialog-${++checkoutDialogCount}`;
      dialog.setAttribute('aria-labelledby', `${id}-title`);
      dialog.setAttribute('aria-describedby', `${id}-detail`);
      const title = document.createElement('h2');
      title.id = `${id}-title`;
      title.textContent = 'Review your cart update';
      const detail = document.createElement('p');
      detail.id = `${id}-detail`;
      detail.setAttribute('role', 'status');
      detail.setAttribute('aria-live', 'polite');
      detail.textContent = gate.message || gate.state?.message || 'A quantity update needs to be checked. Review the current quantities before checkout.';
      const actions = document.createElement('div');
      actions.className = 'kp-utsav-offer-dialog__actions';
      const review = document.createElement('button');
      review.type = 'button';
      review.className = 'btn btn-theme';
      review.dataset.kpUtsavReviewUpdate = '';
      review.textContent = 'Review cart update';
      review.disabled = Boolean(this.quantityReviewPending);
      this.quantityReviewAction = review;
      review.addEventListener('click', async () => {
        if (this.quantityReviewPending) return;
        const current = this.quantityQueueGate();
        if (current.unavailable) { detail.textContent = current.message; return; }
        this.quantityReviewPending = true;
        review.disabled = true;
        review.setAttribute('aria-busy', 'true');
        detail.textContent = 'Checking the current quantities…';
        try {
          const result = await current.queue.checkPendingChange();
          const after = this.quantityQueueGate();
          if (this.checkoutChoice?.dialog !== dialog) return;
          if (after.blocked) {
            detail.textContent = result?.error || after.message || after.state?.message || 'This update still needs review. Check the current cart, then select Review cart update again.';
            return;
          }
          let refreshRequired = false;
          if (result?.cart && this.drawer.isConnected) {
            try { await this.refreshUtsavNativeCart(result.cart, null, window.KpUtsavJourney?.cartRevision?.()); }
            catch (_) { refreshRequired = true; }
          }
          if (this.checkoutChoice?.dialog !== dialog) return;
          if (refreshRequired || this.drawer.dataset.kpFullcartNeedsRefresh === 'true') {
            detail.textContent = 'Cart quantities were checked. Refresh this page to review the changed items before checkout.';
            refresh.focus?.();
            return;
          }
          this.closeCheckoutChoice();
          this.showUtsavStatus('Cart update reviewed. Review your current items and check out again.');
          const checkout = this.drawer.querySelector('.js-cart-btn-checkout') || button;
          checkout?.focus?.();
        } catch (error) {
          if (this.checkoutChoice?.dialog === dialog) detail.textContent = error.message || 'Could not check the update. Please try Review cart update again.';
        } finally {
          this.quantityReviewPending = false;
          review.disabled = false;
          review.removeAttribute('aria-busy');
          if (this.quantityReviewAction) this.quantityReviewAction.disabled = false;
        }
      });
      const refresh = document.createElement('button');
      refresh.type = 'button';
      refresh.className = 'btn btn-outline';
      refresh.textContent = 'Refresh cart page';
      refresh.addEventListener('click', () => window.location.reload());
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'btn btn-outline';
      back.textContent = 'Back to cart';
      back.addEventListener('click', () => this.closeCheckoutChoice(true));
      actions.appendChild(review);
      actions.appendChild(refresh);
      actions.appendChild(back);
      dialog.appendChild(title);
      dialog.appendChild(detail);
      dialog.appendChild(actions);
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeCheckoutChoice(true); });
      dialog.addEventListener('close', () => { if (this.checkoutChoice?.dialog === dialog) this.closeCheckoutChoice(true); });
      document.body.appendChild(dialog);
      this.checkoutChoice = { dialog, button };
      if (typeof dialog.showModal !== 'function') {
        this.closeCheckoutChoice(true);
        this.showUtsavStatus('Use an up-to-date browser to review the cart update, or refresh the page.');
        return;
      }
      dialog.showModal();
      review.focus?.();
    }

    scheduleSync(delay = 80, cart = null) {
      if (cart) this.lastCart = cart;
      window.clearTimeout(this.syncTimer);
      this.syncTimer = window.setTimeout(() => this.sync(), delay);
    }

    async fetchCart() {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 12000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Could not refresh the cart.');
        return await response.json();
      } finally { window.clearTimeout(timeout); }
    }

    checkoutSignature(cart) {
      return JSON.stringify([
        Number(cart.total_price), Number(cart.total_discount || 0), cart.discount_codes || [],
        cart.cart_level_discount_applications || [],
        (cart.items || []).map(item => [item.key, item.quantity, item.original_line_price,
          item.final_line_price ?? item.line_price, item.properties || {}, item.line_level_discount_allocations || []])
      ]);
    }

    hasAppliedBenefit(cart) {
      const lineSavings = (cart.items || []).reduce((sum, item) => sum + actualSavingsForItem(item), 0);
      const orderSavings = (cart.cart_level_discount_applications || [])
        .reduce((sum, application) => sum + Number(application.total_allocated_amount || 0), 0);
      return lineSavings > 0 || orderSavings > 0;
    }

    closeCheckoutChoice(restoreFocus = false) {
      const choice = this.checkoutChoice;
      this.checkoutChoice = null;
      if (!choice) return;
      if (choice.dialog.open) choice.dialog.close();
      choice.dialog.remove();
      if (restoreFocus && choice.button?.isConnected) choice.button.focus?.();
    }

    showCheckoutChoice(message, cart, button) {
      this.showUtsavStatus('');
      const dialog = document.createElement('dialog');
      dialog.className = 'kp-utsav-offer-dialog';
      const id = `kp-utsav-offer-dialog-${++checkoutDialogCount}`;
      dialog.setAttribute('aria-labelledby', `${id}-title`);
      dialog.setAttribute('aria-describedby', `${id}-detail`);
      const heading = document.createElement('h2');
      heading.id = `${id}-title`;
      heading.textContent = 'Review your Utsav offer';
      const text = document.createElement('p');
      text.id = `${id}-detail`;
      text.textContent = `${message} Current cart subtotal: ${money(cart.total_price)}. Taxes and shipping are calculated at checkout.`;
      const actions = document.createElement('div');
      actions.className = 'kp-utsav-offer-dialog__actions';
      const browse = document.createElement('a');
      browse.href = window.KpUtsavJourney?.builderUrl?.() || '/pages/utsav-combo';
      browse.className = 'btn btn-theme';
      browse.textContent = 'Add eligible products';
      browse.addEventListener('click', () => this.closeCheckoutChoice());
      const proceed = document.createElement('button');
      proceed.type = 'button';
      proceed.className = 'btn btn-outline';
      proceed.textContent = this.hasAppliedBenefit(cart) ? 'Continue at current total' : 'Continue at full price';
      const signature = this.checkoutSignature(cart);
      proceed.addEventListener('click', () => {
        this.closeCheckoutChoice();
        this.validateUtsavCheckout(this.drawer.querySelector('.js-cart-btn-checkout') || button, signature);
      });
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'btn btn-outline';
      retry.textContent = 'Recheck offer';
      retry.addEventListener('click', () => {
        this.closeCheckoutChoice();
        this.validateUtsavCheckout(this.drawer.querySelector('.js-cart-btn-checkout') || button);
      });
      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.className = 'btn btn-outline';
      cancel.textContent = 'Back to cart';
      cancel.addEventListener('click', () => this.closeCheckoutChoice(true));
      actions.appendChild(browse);
      actions.appendChild(proceed);
      actions.appendChild(retry);
      actions.appendChild(cancel);
      dialog.appendChild(heading);
      dialog.appendChild(text);
      dialog.appendChild(actions);
      dialog.addEventListener('cancel', event => { event.preventDefault(); this.closeCheckoutChoice(true); });
      dialog.addEventListener('close', () => { if (this.checkoutChoice?.dialog === dialog) this.closeCheckoutChoice(true); });
      document.body.appendChild(dialog);
      this.checkoutChoice = { dialog, button };
      if (typeof dialog.showModal !== 'function') {
        this.closeCheckoutChoice(true);
        this.showUtsavStatus('Please use an up-to-date browser to review the offer before checkout.');
        return;
      }
      dialog.showModal();
      browse.focus?.();
    }

    async validateUtsavCheckout(button, continueSignature = null) {
      if (this.utsavCheckoutPending || !button?.isConnected) return;
      if (this.quantityQueueGate().blocked) { this.showQuantityReview(button); return; }
      this.utsavCheckoutPending = true;
      button.setAttribute('aria-busy', 'true');
      let cart = null;
      let eligibleItems = null;
      let revision;
      try {
        const journey = window.KpUtsavJourney;
        let stable = false;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          this.assertQuantityQueueSettled();
          if (journey?.isMutating?.()) throw new Error('Your cart is updating. Please try again.');
          revision = journey?.cartRevision?.();
          cart = await this.fetchCart();
          this.assertQuantityQueueSettled();
          if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.total_price) || cart.total_price < 0) {
            cart = null;
            throw new Error('We could not verify your cart total. Please reload and try again.');
          }
          if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) { cart = null; continue; }
          stable = true;
          break;
        }
        if (!stable) throw new Error('Your cart changed. Please review it and try again.');
        if (!Number(cart.item_count || 0)) { this.showUtsavStatus('Your cart is empty. Add an item to continue.'); return; }
        const continued = continueSignature !== null && continueSignature === this.checkoutSignature(cart);
        eligibleItems = continued || this.hasAppliedBenefit(cart) ? null : await this.getEligibleItems(cart);
        this.assertQuantityQueueSettled();
        if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) { cart = null; throw new Error('Your cart changed. Please review it and try again.'); }
        if (eligibleItems) this.render(cart, eligibleItems);
        if (continueSignature !== null && !continued) {
          this.showCheckoutChoice('Your cart changed since the warning. Please review the updated items, applied savings and total before continuing.', cart, button);
          return;
        }
        const eligibility = this.offerEligibility(cart, eligibleItems || []);
        const { subtotal, qualifies: qualified } = eligibility;
        if (!continued && !this.hasAppliedBenefit(cart)) {
          if (!qualified) {
            this.showCheckoutChoice(`The Utsav offer needs ${money(Math.max(this.minimumSubtotal - subtotal, 0))} more in eligible products before discount. You can add items or continue without this offer.`, cart, button);
            return;
          }
          if (typeof journey?.ensureOfferDiscount !== 'function') throw new Error('We could not verify the Utsav offer. Recheck it or continue without this offer.');
          const verified = await journey.ensureOfferDiscount(cart, candidate => this.getEligibleItems(candidate));
          this.assertQuantityQueueSettled();
          cart = verified.cart;
          revision = verified.revision;
          if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) { cart = null; throw new Error('Your cart changed. Please review it and try again.'); }
          eligibleItems = this.hasAppliedBenefit(cart) ? null : await this.getEligibleItems(cart);
          if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) { cart = null; throw new Error('Your cart changed. Please review it and try again.'); }
          if (!this.hasAppliedBenefit(cart)) throw new Error('The Utsav offer was not applied. Recheck it or continue without this offer.');
        }
        await this.handoffVerifiedCart(cart, eligibleItems, revision);
      } catch (error) {
        if (error.quantityQueueBlocked) { this.showQuantityReview(button); return; }
        const journey = window.KpUtsavJourney;
        if (error.checkoutStateUncertain) cart = null;
        else if (cart && !journey?.isMutating?.()) {
          cart = null;
          try {
            revision = journey?.cartRevision?.();
            const current = await this.fetchCart();
            if (!journey?.isMutating?.() && journey?.cartRevision?.() === revision
              && Array.isArray(current?.items) && Number.isSafeInteger(current.total_price) && current.total_price >= 0) cart = current;
          } catch (_) {}
        }
        if (cart && this.hasAppliedBenefit(cart)
          && !journey?.isMutating?.() && journey?.cartRevision?.() === revision) {
          try {
            await this.handoffVerifiedCart(cart, null, revision);
            return;
          } catch (recoveryError) {
            if (recoveryError.quantityQueueBlocked) { this.showQuantityReview(button); return; }
            cart = null;
            error = recoveryError;
          }
        }
        if (cart && !journey?.isMutating?.() && journey?.cartRevision?.() === revision) {
          this.showCheckoutChoice(error.message || 'We could not verify the Utsav offer.', cart, button);
        } else {
          this.showUtsavStatus(error.message || 'We could not verify your cart. Please try again.');
        }
      } finally {
        this.utsavCheckoutPending = false;
        button.removeAttribute('aria-busy');
      }
    }

    async handoffVerifiedCart(cart, eligibleItems, revision) {
      this.assertQuantityQueueSettled();
      const journey = window.KpUtsavJourney;
      await this.refreshUtsavNativeCart(cart, eligibleItems, revision);
      this.assertQuantityQueueSettled();
      const finalCart = await this.fetchCart();
      this.assertQuantityQueueSettled();
      if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) {
        throw Object.assign(new Error('Your cart changed. Please review it and try again.'), { checkoutStateUncertain: true });
      }
      if (!finalCart || !Array.isArray(finalCart.items) || !Number.isSafeInteger(finalCart.total_price) || finalCart.total_price < 0) {
        throw Object.assign(new Error('We could not verify your cart total. Please reload and try again.'), { checkoutStateUncertain: true });
      }
      if (this.checkoutSignature(finalCart) !== this.checkoutSignature(cart)) {
        try {
          const currentEligible = await this.getEligibleItems(finalCart);
          await this.refreshUtsavNativeCart(finalCart, currentEligible, revision);
        } catch (_) {
          if (this.recommendations) this.recommendations.hidden = true;
          this.offer.hidden = true;
        }
        throw Object.assign(new Error('Your cart changed before checkout. Please review it and try again.'), { checkoutStateUncertain: true });
      }
      this.showUtsavStatus('');
      const replayButton = this.drawer.querySelector('.js-cart-btn-checkout');
      if (!replayButton?.isConnected) throw new Error('Please reopen your cart and try checking out again.');
      try { this.replayCheckout(replayButton, revision, cart); }
      catch (error) { error.checkoutStateUncertain = true; throw error; }
    }

    replayCheckout(button, revision) {
      this.assertQuantityQueueSettled();
      this.utsavCheckoutPermit = { button, revision, synchronous: true };
      try { button.click(); }
      finally { this.utsavCheckoutPermit = null; }
    }

    async refreshUtsavNativeCart(cart, eligibleItems, revision) {
      const journey = window.KpUtsavJourney;
      if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) throw new Error('Your selection changed. Please review it and try again.');
      if (typeof window.theme?.miniCart?.refreshCart !== 'function') throw new Error('We could not refresh your cart safely. Please reload and try again.');
      await window.theme.miniCart.refreshCart(cart, false);
      if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) throw new Error('Your selection changed. Please review it and try again.');
      this.checkoutButton = this.drawer.querySelector('.js-cart-btn-checkout');
      journey?.observeCart?.(cart);
      if (eligibleItems) this.render(cart, eligibleItems);
      else {
        this.offer.hidden = true;
        if (this.recommendations) this.recommendations.hidden = true;
        this.renderSavings(cart, Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0)));
        this.decorateCartLines(cart, []);
      }
    }

    showUtsavStatus(message) {
      this.closeCheckoutChoice();
      if (message && this.drawer.classList.contains('cardraw') && !this.isOpen()) this.drawer.classList.add('active');
      let status = this.drawer.querySelector('[data-utsav-checkout-status]');
      if (!message) { status?.remove(); return; }
      if (!status) {
        status = document.createElement('p');
        status.dataset.utsavCheckoutStatus = '';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.className = 'alert alert-warning';
        this.offer.after(status);
      }
      status.textContent = message;
    }

    async fetchProduct(handle) {
      if (!handle) return null;
      if (this.productCache.has(handle)) return this.productCache.get(handle);

      const promise = fetch(`/products/${encodeURIComponent(handle)}.js`, {
        cache: 'force-cache',
        headers: { Accept: 'application/json' }
      })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null);

      this.productCache.set(handle, promise);
      return promise;
    }

    async getEligibleItems(cart) {
      const journey = window.KpUtsavJourney;
      if (typeof journey?.resolveEligibleItems !== 'function') {
        throw new Error('We could not verify which products qualify. Please recheck the offer.');
      }
      const evaluated = typeof journey.evaluateEligibility === 'function'
        ? await journey.evaluateEligibility(cart) : null;
      const resolved = evaluated ? evaluated.items : await journey.resolveEligibleItems(cart);
      if (!Array.isArray(resolved) || new Set(resolved).size !== resolved.length
        || resolved.some(item => !(cart.items || []).includes(item))) {
        throw new Error('We could not verify which products qualify. Please recheck the offer.');
      }
      if (evaluated) {
        if (!Number.isSafeInteger(evaluated.units) || evaluated.units < 0
          || !Number.isSafeInteger(evaluated.subtotal) || evaluated.subtotal < 0
          || typeof evaluated.qualifies !== 'boolean') throw new Error('We could not verify the offer total. Please recheck it.');
        this.eligibilitySnapshot = { cart, ...evaluated };
      }
      return resolved;
    }

    offerEligibility(cart, items) {
      if (this.eligibilitySnapshot?.cart === cart && this.eligibilitySnapshot.items === items) return this.eligibilitySnapshot;
      const units = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const subtotal = items.reduce((sum, item) => sum + Number(item.original_line_price ?? item.line_price ?? 0), 0);
      return { items, units, subtotal, qualifies: Number.isSafeInteger(units) && units > 0
        && Number.isSafeInteger(subtotal) && subtotal >= this.minimumSubtotal };
    }

    async sync(cart = null) {
      if (!this.isOpen()) return;
      if (this.isSyncing) {
        if (cart) this.lastCart = cart;
        this.hasPendingSync = true;
        return;
      }
      this.isSyncing = true;

      try {
        const journey = window.KpUtsavJourney;
        if (journey?.isMutating?.()) { this.hasPendingSync = true; return; }
        const revision = journey?.cartRevision?.();
        let nextCart = cart || this.lastCart || await this.fetchCart();
        this.lastCart = null;
        let eligibleItems = await this.getEligibleItems(nextCart);
        if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) {
          this.hasPendingSync = true;
          return;
        }
        journey?.observeCart(nextCart);
        this.render(nextCart, eligibleItems);
      } catch (error) {
        if (this.recommendationStatus) {
          this.recommendationStatus.textContent = error.message || (window.KpUtsavJourney?.isActive()
            ? 'Could not refresh your Utsav selection.' : 'Could not refresh offer savings.');
        }
      } finally {
        this.isMutating = false;
        this.isSyncing = false;
        if (this.hasPendingSync || this.lastCart) {
          this.hasPendingSync = false;
          this.scheduleSync(80);
        }
      }
    }

    celebrateQualification(cart, qualifies) {
      const newlyQualified = qualifies && !lastCartQualified;
      lastCartQualified = qualifies;
      if (newlyQualified && !document.querySelector('.combo-page--utsav')) this.showQualificationCelebration();
    }

    showQualificationCelebration() {
      this.offer.querySelector('[data-kp-utsav-celebration]')?.remove();
      const celebration = document.createElement('p');
      celebration.dataset.kpUtsavCelebration = '';
      celebration.setAttribute('role', 'status');
      celebration.setAttribute('aria-live', 'polite');
      celebration.style.position = 'relative';
      celebration.style.pointerEvents = 'none';
      celebration.textContent = 'Your selection qualifies for the Utsav offer.';
      if (!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
        for (let index = 0; index < 12; index += 1) {
          const piece = document.createElement('span');
          piece.setAttribute('aria-hidden', 'true');
          piece.style.position = 'absolute';
          piece.style.left = `${8 + index * 7}%`;
          piece.style.top = '0';
          piece.style.width = '5px';
          piece.style.height = '7px';
          piece.style.backgroundColor = ['#c8a24a', '#2d7a45', '#b86f52'][index % 3];
          celebration.appendChild(piece);
          piece.animate?.([{ transform: 'translateY(-8px) rotate(0deg)', opacity: 1 },
            { transform: `translateY(32px) rotate(${90 + index * 25}deg)`, opacity: 0 }],
          { duration: 900, delay: index * 20, fill: 'forwards' });
        }
      }
      this.offer.appendChild(celebration);
      window.setTimeout(() => celebration.remove(), 2200);
    }

    render(cart, eligibleItems) {
      this.renderedCart = cart;
      this.renderUtsav(cart, eligibleItems);
    }

    renderUtsav(cart, eligibleItems) {
      this.offer.dataset.kpUtsavReady = 'true';
      this.recommendations.dataset.kpUtsavReady = 'true';
      const totalLabel = this.drawer.querySelector('.mini-cart-footer .bottom-total');
      const textNode = totalLabel && Array.from(totalLabel.childNodes).find((node) => node.nodeType === 3 && node.textContent.trim());
      if (textNode) textNode.textContent = 'Subtotal: ';
      const { units, subtotal, qualifies: ready } = this.offerEligibility(cart, eligibleItems);
      this.celebrateQualification(cart, ready);
      const remainingValue = Math.max(this.minimumSubtotal - subtotal, 0);
      const savings = eligibleItems.reduce((sum, item) => sum + utsavSavingsForItem(item), 0);
      const applied = ready && eligibleItems.length > 0 && eligibleItems.every(item => {
        const original = Number(item.original_line_price ?? item.line_price ?? 0);
        const saving = utsavSavingsForItem(item);
        return original > 0 && saving >= Math.floor(original / 5) && saving <= Math.ceil(original / 5);
      });
      const title = applied ? `${this.discountPercentage}% Utsav offer applied`
          : ready ? 'Your Utsav selection qualifies'
            : `Add ${money(remainingValue)} more in eligible products`;
      this.drawer.classList.toggle('has-kp-sidecart-offer', Number(cart.item_count || 0) > 0);
      this.offer.hidden = Number(cart.item_count || 0) === 0;
      this.offer.classList.toggle('is-applied', applied);
      this.offer.classList.toggle('is-ready', ready && !applied);
      this.offer.querySelector('[data-kp-sidecart-offer-count]').textContent = applied ? '✓' : '20%';
      this.offer.querySelector('[data-kp-sidecart-offer-eyebrow]').textContent = 'Utsav offer';
      this.offer.querySelector('[data-kp-sidecart-offer-title]').textContent = title;
      this.offer.querySelector('[data-kp-sidecart-offer-detail]').textContent = applied
        ? `You saved ${money(savings)} on your eligible Utsav products. 18K/22K gold jewellery, gift boxes and gift wrap are excluded from the ₹6,999 minimum and the 20% discount.`
        : 'Save 20% on eligible products worth ₹6,999 or more. Choose one piece or mix your favourites. 18K/22K gold jewellery, gift boxes and gift wrap are excluded from the ₹6,999 minimum and the 20% discount. Taxes and shipping calculated at checkout.';
      this.offer.querySelector('[data-kp-sidecart-offer-progress-copy]').textContent = `${units} eligible piece${units === 1 ? '' : 's'} · ${money(subtotal)} of ₹6,999 before discount`;
      const progress = Math.max(0, Math.min(subtotal / 699900, 1));
      const progressPercent = ready ? 100 : Math.min(99, Math.floor(progress * 100));
      const progressElement = this.offer.querySelector('[data-kp-sidecart-offer-progress]');
      progressElement.setAttribute('aria-valuemax', '100');
      progressElement.setAttribute('aria-valuenow', String(progressPercent));
      progressElement.setAttribute('aria-label', 'Progress towards ₹6,999 in eligible products before discount');
      this.offer.querySelector('[data-kp-sidecart-offer-progress-fill]').style.width = `${progressPercent}%`;
      if (this.offerExpiryCopy) this.offerExpiryCopy.textContent = '₹6,999 eligible products minimum';
      this.renderSavings(cart, Math.max(0, Number(cart.original_total_price || 0) - Number(cart.total_price || 0)));
      this.savings.querySelector('[data-kp-sidecart-original-total]').textContent = money(cart.original_total_price ?? cart.total_price);
      this.decorateCartLines(cart, eligibleItems);
      if (this.hamperCard) this.hamperCard.hidden = true;
      this.recommendations.hidden = Number(cart.item_count || 0) === 0;
      this.recommendations.querySelector('[data-kp-sidecart-recommendations-title]').textContent = 'Utsav offer';
      this.recommendations.querySelector('[data-kp-sidecart-recommendations-copy]').textContent = 'Choose one piece or mix your favourites';
      this.recommendationTrack.replaceChildren();
      const link = document.createElement('a');
      link.className = 'btn btn-theme';
      link.textContent = 'Shop eligible products';
      link.href = window.KpUtsavJourney?.builderUrl?.() || '/pages/utsav-combo';
      link.style.minHeight = '44px';
      this.recommendationTrack.appendChild(link);
    }

    renderSavings(cart, comboSavings) {
      if (!comboSavings) {
        this.savings.hidden = true;
        this.restoreCheckoutLabel();
        return;
      }

      this.savings.hidden = false;
      this.savings.querySelector('[data-kp-sidecart-original-total]').textContent = money(
        Number(cart.original_total_price || 0)
      );
      this.savings.querySelector('[data-kp-sidecart-savings-total]').textContent = `−${money(comboSavings)}`;
      if (this.checkoutButton) {
        this.checkoutButton.textContent = `CHECK OUT · SAVE ${money(comboSavings)}`;
      }
    }

    restoreCheckoutLabel() {
      if (this.checkoutButton) this.checkoutButton.textContent = 'CHECK OUT';
    }

    renderOfferExpiry() {
      if (!this.offerExpiryCopy) return;
      const expiresAt = this.offerEndDate
        ? new Date(`${this.offerEndDate}T23:59:59+05:30`)
        : null;
      const active = expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() > Date.now();
      this.offerExpiryCopy.textContent = active
        ? `Ends ${new Intl.DateTimeFormat('en-IN', {
            day: 'numeric',
            month: 'short',
            timeZone: 'Asia/Kolkata'
          }).format(expiresAt)}`
        : 'No code needed';
    }

    decorateCartLines(cart, eligibleItems) {
      const eligibleKeys = new Set(eligibleItems.map((item) => item.key));
      const itemsByKey = new Map((cart.items || []).map((item) => [item.key, item]));
      const itemNodes = this.cartContent.querySelectorAll('.mini-cart-item');

      itemNodes.forEach((node) => {
        const item = itemsByKey.get(node.dataset.cartKey);
        if (!item) return;
        node.querySelectorAll('.kp-sidecart-line-offer').forEach((element) => element.remove());

        const meta = node.querySelector('.mini-cart-meta');
        const price = meta?.querySelector(':scope > span');
        if (!meta || !price) return;

        const itemSaving = actualSavingsForItem(item);
        price.classList.toggle('kp-sidecart-line-price', itemSaving > 0);

        if (itemSaving > 0) {
          price.innerHTML = `<del>${money(item.original_price)}</del><strong>${money(item.final_price)}</strong>`;
        } else {
          price.textContent = money(item.final_price ?? item.price);
        }

        if (itemSaving > 0 || eligibleKeys.has(item.key)) {
          const badge = document.createElement('small');
          badge.className = 'kp-sidecart-line-offer';
          badge.textContent = itemSaving > 0 ? `Cart saving ${money(itemSaving)}` : 'Eligible for the Utsav offer';
          meta.appendChild(badge);
        }
      });
    }

    isRakhiAddon(item) {
      return item?.handle === 'rakhi-giftbox'
        || String(item?.properties?._rakhi_addon) === 'true';
    }

    renderHamperControl(cart) {
      const items = cart.items || [];
      const addonItems = items.filter((item) => this.isRakhiAddon(item));
      const regularItems = items.filter((item) => !this.isRakhiAddon(item));
      const quantity = addonItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      if (this.hamperCard) this.hamperCard.hidden = regularItems.length === 0 && quantity === 0;
      if (this.hamperQuantity) this.hamperQuantity.textContent = String(quantity);
      if (this.hamperAdd) {
        this.hamperAdd.hidden = quantity > 0;
        this.hamperAdd.disabled = this.isMutating;
      }
      const quantityControl = this.hamperQuantity?.closest('.kp-sidecart-hamper-card__quantity');
      if (quantityControl) quantityControl.hidden = quantity === 0;
      if (this.hamperMinus) this.hamperMinus.disabled = this.isMutating || quantity === 0;
      if (this.hamperPlus) this.hamperPlus.disabled = this.isMutating;
    }

    async changeHamperQuantity(change) {
      if (this.isMutating || !change) return;
      this.isMutating = true;
      if (this.hamperAdd) this.hamperAdd.disabled = true;
      if (this.hamperMinus) this.hamperMinus.disabled = true;
      if (this.hamperPlus) this.hamperPlus.disabled = true;

      try {
        let cart = await this.fetchCart();
        const addonItems = (cart.items || []).filter((item) => this.isRakhiAddon(item));

        if (change > 0) {
          const hamper = await this.fetchProduct('rakhi-giftbox');
          const variant = hamper?.variants?.find((candidate) => candidate.available);
          if (!variant) throw new Error('The Festive Giftbox is currently unavailable.');

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{
                id: Number(variant.id),
                quantity: 1,
                properties: {
                  _rakhi_addon: 'true',
                  _rakhi_source: 'sidecart',
                  _combo_excluded: 'true'
                }
              }]
            })
          });
          if (!response.ok) throw new Error('Could not add the Festive Giftbox.');
        } else {
          const target = addonItems.find((item) => (
            item.properties?._rakhi_source === 'sidecart'
            && !item.properties?._rakhi_parent_group
            && !item.properties?._rakhi_parent_key
            && !item.properties?._rakhi_parent_variant
          )) || addonItems[addonItems.length - 1];
          if (!target) return;

          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: target.key,
              quantity: Math.max(0, Number(target.quantity || 0) - 1)
            })
          });
          if (!response.ok) throw new Error('Could not remove the Festive Giftbox.');
        }

        cart = await this.fetchCart();
        window.theme?.miniCart?.refreshCart?.(cart, false);
        document.dispatchEvent(new CustomEvent('kp:cart-updated', { detail: { cart } }));
        this.drawer.classList.add('active');
        this.renderHamperControl(cart);
      } catch (error) {
        window.alert(error.message || 'Could not update the Festive Giftbox.');
      } finally {
        this.isMutating = false;
        this.scheduleSync(0);
      }
    }

    refreshThemeCart() {
      window.theme?.miniCart?.updateElements?.();
      window.theme?.miniCart?.generateCart?.();
      this.drawer.classList.add('active');
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = String(value || '');
      return element.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/`/g, '&#96;');
    }
  }

  class CartPageCheckoutGuard extends SidecartComboCro {
    constructor(form) {
      super(form);
      this.form = form;
      this.offer = form.querySelector('[data-kp-cart-offer]');
      this.checkoutButton = form.querySelector('button[name="checkout"]');
      this.minimumSubtotal = 699900;
      if (!this.offer || !this.checkoutButton) return;
      this.bind();
      this.schedulePageSync();
    }

    bind() {
      this.cartPageCheckoutCapture = event => {
        const button = event.target?.closest?.('button[name="checkout"]');
        if (!button || !this.form.isConnected || button.form !== this.form) return;
        if (this.quantityQueueGate().blocked) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.formCheckoutPermit = null;
          this.showQuantityReview(button);
          return;
        }
        if (button.disabled || button.classList.contains('disabled')
          || button.getAttribute('aria-disabled') === 'true') return;
        const permit = this.formCheckoutPermit;
        if (permit?.button === button && permit.synchronous === true && !permit.clickSeen) {
          permit.clickSeen = true;
          try {
            this.verifyFormPermit(permit);
            return;
          } catch (error) { this.showUtsavStatus(error.message); }
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!permit) this.validateUtsavCheckout(button);
      };
      window.addEventListener('click', this.cartPageCheckoutCapture, true);
      this.form.addEventListener('submit', event => {
        const button = event.submitter;
        if (!button || button.name !== 'checkout' || button.form !== this.form) return;
        if (this.quantityQueueGate().blocked) {
          event.preventDefault(); event.stopImmediatePropagation();
          this.formCheckoutPermit = null;
          this.showQuantityReview(button);
          return;
        }
        const permit = this.formCheckoutPermit;
        if (permit?.button === button && permit.synchronous === true && !permit.submitSeen) {
          permit.submitSeen = true;
          try {
            this.verifyFormPermit(permit);
            permit.inputs = Array.from(this.form.querySelectorAll('input[name="updates[]"]'));
            permit.disabled = permit.inputs.map(input => input.disabled);
            permit.inputs.forEach(input => { input.disabled = true; });
            return;
          } catch (error) { this.showUtsavStatus(error.message); }
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!permit) this.validateUtsavCheckout(button);
      }, true);
      const rows = this.form.querySelector('#CartProducts');
      if (rows) new MutationObserver(() => this.schedulePageSync()).observe(rows, { childList: true });
      document.addEventListener('kp:cart-updated', () => this.schedulePageSync());
      this.form.dataset.kpUtsavCheckoutGuardReady = 'true';
    }

    verifyFormPermit(permit) {
      this.assertQuantityQueueSettled();
      const journey = window.KpUtsavJourney;
      if (journey?.isMutating?.() || journey?.cartRevision?.() !== permit.revision) throw new Error('Your cart changed. Please review it and try again.');
      this.assertFormMatchesCart(permit.cart);
    }

    schedulePageSync() {
      window.clearTimeout(this.pageSyncTimer);
      this.pageSyncTimer = window.setTimeout(() => this.syncPageCart(), 80);
    }

    async syncPageCart() {
      if (!this.form.isConnected || this.pageSyncPending || this.utsavCheckoutPending) return;
      const journey = window.KpUtsavJourney;
      if (journey?.isMutating?.()) return;
      this.pageSyncPending = true;
      try {
        const revision = journey?.cartRevision?.();
        const cart = await this.fetchCart();
        const items = await this.getEligibleItems(cart);
        if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) return;
        await this.refreshUtsavNativeCart(cart, items, revision);
      } catch (error) {
        this.showUtsavStatus(error.message || 'Could not verify your cart. Please refresh this page.');
      } finally { this.pageSyncPending = false; }
    }

    assertFormMatchesCart(cart) {
      if (this.form.dataset.kpFullcartNeedsRefresh === 'true') throw new Error('Your cart has changed items. Refresh this page to review them before checkout.');
      const rows = Array.from(this.form.querySelectorAll('#CartProducts > tr[data-cart-key]'));
      const byKey = new Map((cart.items || []).map(item => [item.key, item]));
      const seen = new Set();
      if (rows.length !== byKey.size) throw new Error('Your cart changed. Refresh this page to review every item before checkout.');
      for (const row of rows) {
        const item = byKey.get(row.dataset.cartKey);
        if (!item || seen.has(item.key)) throw new Error('Your cart changed. Refresh this page to review every item before checkout.');
        seen.add(item.key);
        const inputs = Array.from(row.querySelectorAll('input[data-kp-mini-quantity], input[name="updates[]"]'));
        if (inputs.some(input => !Number.isSafeInteger(Number(input.value)) || Number(input.value) !== Number(item.quantity))) {
          throw new Error('Your quantity update is still pending. Wait for the update or refresh the page, then review your cart before checkout.');
        }
        if (!inputs.length && Number(row.dataset.kpCartQuantity) !== Number(item.quantity)) {
          throw new Error('Your add-on quantity changed. Refresh this page to review it before checkout.');
        }
      }
    }

    render(cart, eligibleItems) {
      const progress = this.offer.querySelector('[data-kp-cart-offer-progress]');
      if (eligibleItems) {
        const eligibility = this.offerEligibility(cart, eligibleItems);
        const percent = eligibility.qualifies ? 100 : Math.min(99, Math.floor(Math.max(0, eligibility.subtotal / this.minimumSubtotal) * 100));
        progress.hidden = false;
        progress.value = percent;
        this.celebrateQualification(cart, eligibility.qualifies);
        this.offer.querySelector('[data-kp-cart-offer-summary]').textContent = `${money(eligibility.subtotal)} of ₹6,999 in eligible products before discount`;
      } else {
        progress.hidden = true;
        this.offer.querySelector('[data-kp-cart-offer-summary]').textContent = 'Review your verified subtotal and applied savings below. Eligibility progress is unavailable.';
      }
      this.offer.hidden = false;
      this.offer.querySelector('[data-kp-cart-before-discounts]').textContent = money(cart.original_total_price);
      this.offer.querySelector('[data-kp-cart-actual-savings]').textContent = money(Math.max(0, Number(cart.original_total_price) - Number(cart.total_price)));
      this.offer.querySelector('[data-kp-cart-current-subtotal]').textContent = money(cart.total_price);
    }

    async refreshUtsavNativeCart(cart, eligibleItems, revision) {
      const journey = window.KpUtsavJourney;
      if (journey?.isMutating?.() || journey?.cartRevision?.() !== revision) throw new Error('Your cart changed. Please review it and try again.');
      this.assertFormMatchesCart(cart);
      const byKey = new Map((cart.items || []).map(item => [item.key, item]));
      this.form.querySelectorAll('#CartProducts > tr[data-cart-key]').forEach(row => {
        const item = byKey.get(row.dataset.cartKey);
        row.querySelectorAll('[data-kp-cart-unit-price]').forEach(node => { node.textContent = money(item.final_price ?? item.price); });
        row.querySelectorAll('[data-kp-cart-line-prices]').forEach(container => {
          container.replaceChildren();
          if (Number(item.original_line_price) !== Number(item.final_line_price ?? item.line_price)) {
            const original = document.createElement('del');
            original.dataset.kpCartOriginalLineTotal = '';
            original.textContent = money(item.original_line_price);
            container.appendChild(original);
            container.appendChild(document.createElement('br'));
          }
          const actual = document.createElement('span');
          actual.className = 'cart__item-total text-body';
          actual.dataset.kpCartLineTotal = '';
          actual.textContent = money(item.final_line_price ?? item.line_price);
          container.appendChild(actual);
        });
      });
      const total = this.form.querySelector('#CartSubtotal');
      if (total) total.textContent = money(cart.total_price);
      const savings = this.form.querySelector('[data-kp-cart-current-savings]');
      if (savings) savings.textContent = Number(cart.original_total_price) > Number(cart.total_price)
        ? `Cart savings: ${money(Number(cart.original_total_price) - Number(cart.total_price))}` : '';
      this.render(cart, eligibleItems);
      journey?.observeCart?.(cart);
    }

    replayCheckout(button, revision, cart) {
      const permit = { button, revision, cart, synchronous: true, clickSeen: false, submitSeen: false };
      this.verifyFormPermit(permit);
      if (typeof button.click !== 'function') throw new Error('Please refresh your browser and try checking out again.');
      this.formCheckoutPermit = permit;
      try { button.click(); }
      finally {
        this.formCheckoutPermit = null;
        (permit.inputs || []).forEach((input, index) => { input.disabled = permit.disabled[index]; });
      }
    }

  }

  const init = () => {
    document.querySelectorAll('form[data-kp-cart-checkout-guard]').forEach(form => {
      if (form.dataset.kpUtsavCheckoutInitialized === 'true') return;
      form.dataset.kpUtsavCheckoutInitialized = 'true';
      new CartPageCheckoutGuard(form);
    });
    document.querySelectorAll('.js-mini-cart.cardraw').forEach((drawer) => {
      if (drawer.dataset.kpSidecartComboInitialized === 'true') return;
      drawer.dataset.kpSidecartComboInitialized = 'true';
      new SidecartComboCro(drawer);
    });
  };

  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
